import { type DeviceNode, deviceNodeBox, deviceView, type TextNode } from "../../templates/builder";
import type { BlockNode, LibraryBlock } from "../build";
import {
  CARD_SHADOW,
  device,
  ellipse,
  icon,
  iconPill,
  motion,
  rect,
  SHADOW,
  stack,
  text,
  WHITE,
} from "./helpers";

// ---------------------------------------------------------------------------
// Mockups: devices with empty screens (the user drops a screenshot onto them)
// in pairs, with headlines, store badges and floating cards.
// ---------------------------------------------------------------------------

const PHONE = "iphone-17-pro";
const LAPTOP = "macbook-pro-14";

/** Ready-made views (`devicePerspectives`), so the editor shows them as chosen. */
const FRONT = deviceView(PHONE, "front");
const HERO = deviceView(PHONE, "hero");
const FLOAT = deviceView(PHONE, "float");
const LYING = deviceView(PHONE, "lying");

/** Soft, deep shadow under devices (follows their outline). */
const DEVICE_SHADOW = { color: "#00000047", y: 30, blur: 70 };

type DeviceSpec = Pick<DeviceNode, "device" | "landscape" | "tilt" | "url">;

/** Width of a device at a height (reference px). */
const widthAt = (spec: DeviceSpec, h: number) => deviceNodeBox({ ...spec, x: 0, y: 0, h }).w;
/** Height of a device at a width (reference px). */
const heightAt = (spec: DeviceSpec, w: number) => deviceNodeBox({ ...spec, x: 0, y: 0, w }).h;

type Item = Omit<TextNode, "x" | "y" | "w">;
const line = (
  value: string,
  style: TextNode["style"],
  size: number,
  color: string,
  extra: Partial<Item> = {},
): Item => ({ kind: "text", text: value, style, size, color, ...extra });

/** White text straight on the video. */
const ON_VIDEO = { shadow: SHADOW };

/** Store badge: black pill with the store's logo, a small and a large line. */
function storeBadge(
  x: number,
  y: number,
  store: "apple" | "google",
  move: Partial<ReturnType<typeof motion>> = {},
): BlockNode[] {
  const w = 255;
  const h = 88;
  return [
    rect({
      x,
      y,
      w,
      h,
      fill: "#000000",
      radius: 20,
      stroke: { color: "#ffffff33", width: 2 },
      name: store === "apple" ? "App Store" : "Google Play",
      ...move,
    }),
    icon({
      icon: store === "apple" ? "apple-logo" : "google-play-logo",
      x: x + 20,
      y: y + 22,
      w: 44,
      h: 44,
      color: WHITE,
      name: "Symbol",
      ...move,
    }),
    text({
      text: store === "apple" ? "Laden im" : "Jetzt bei",
      style: "caption",
      size: 19,
      color: "#ffffffd9",
      x: x + 76,
      y: y + 12,
      w: w - 90,
      shrink: true,
      ...move,
    }),
    text({
      text: store === "apple" ? "App Store" : "Google Play",
      style: "heading-2",
      size: 34,
      color: WHITE,
      x: x + 76,
      y: y + 34,
      w: w - 90,
      shrink: true,
      ...move,
    }),
  ];
}

/** A floating card with a round badge on the left and two lines. */
function floatingCard(
  x: number,
  y: number,
  w: number,
  options: { big: string; small: string; badge: BlockNode[]; move: ReturnType<typeof motion> },
): BlockNode[] {
  const h = 140;
  return [
    rect({
      x,
      y,
      w,
      h,
      fill: "background",
      radius: 32,
      shadow: CARD_SHADOW,
      name: "Karte",
      ...options.move,
    }),
    ...options.badge,
    text({
      text: options.big,
      style: "heading-2",
      size: 44,
      color: "text",
      x: x + 136,
      y: y + 22,
      w: w - 160,
      shrink: true,
      ...options.move,
    }),
    text({
      text: options.small,
      style: "caption",
      size: 24,
      color: "text-muted",
      x: x + 136,
      y: y + 80,
      w: w - 160,
      shrink: true,
      ...options.move,
    }),
  ];
}

const phoneFront = { device: PHONE, tilt: FRONT };
const phoneHero = { device: PHONE, tilt: HERO };
const phoneFloat = { device: PHONE, tilt: FLOAT };

export const MOCKUPS: LibraryBlock[] = [
  {
    id: "mockup-handy-schlagzeile",
    title: "Handy mit Schlagzeile",
    category: "mockup",
    tags: ["App", "Smartphone", "iPhone", "Screenshot", "Neue App", "Launch"],
    width: 1000,
    height: 760,
    anchor: "center",
    only: "design",
    nodes: [
      stack({
        x: 0,
        y: 330,
        w: 540,
        anchor: "center",
        gap: [20, 28],
        items: [
          line("NEUE APP", "subline", 26, "primary"),
          line("Deine Termine. Endlich im Griff.", "heading-1", 72, "text"),
          line("Planen, erinnern, teilen – alles in einer App.", "body", 32, "text-muted"),
        ],
      }),
      rect({ x: 0, y: 624, w: 300, h: 84, fill: "primary", radius: "full", name: "Knopf" }),
      text({
        text: "Jetzt laden",
        style: "subline",
        size: 32,
        color: "on:primary",
        x: 24,
        y: 624,
        w: 252,
        h: 84,
        align: "center",
      }),
      device({
        ...phoneFront,
        x: 1000 - widthAt(phoneFront, 760),
        y: 0,
        h: 760,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
      }),
    ],
  },
  {
    id: "mockup-handy-schraeg",
    title: "Handy schräg mit Text",
    category: "mockup",
    tags: ["App", "Smartphone", "iPhone", "Perspektive", "3D", "Screenshot"],
    width: 900,
    height: 1160,
    anchor: "center",
    only: "design",
    nodes: [
      device({
        ...phoneHero,
        x: (900 - widthAt(phoneHero, 820)) / 2,
        y: 0,
        h: 820,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
      }),
      stack({
        x: 40,
        y: 880,
        w: 820,
        align: "center",
        gap: 18,
        items: [
          line("Plane deine Woche in Minuten", "heading-1", 62, "text"),
          line("Neu: Wochenansicht mit Erinnerungen", "body", 32, "text-muted"),
        ],
      }),
    ],
  },
  {
    id: "mockup-zwei-handys",
    title: "Zwei Handys",
    category: "mockup",
    tags: ["App", "Smartphone", "iPhone", "Paar", "Screenshots", "Vergleich"],
    width: 720,
    height: 980,
    anchor: "center",
    nodes: [
      device({
        ...phoneFloat,
        x: 0,
        y: 0,
        h: 860,
        shadow: DEVICE_SHADOW,
        name: "iPhone hinten",
        ...motion("fly-up", { duration: 700 }),
      }),
      device({
        ...phoneFloat,
        finish: "tiefblau",
        x: 720 - widthAt(phoneFloat, 860),
        y: 120,
        h: 860,
        shadow: DEVICE_SHADOW,
        name: "iPhone vorne",
        ...motion("fly-up", { delay: 250, duration: 700 }),
      }),
    ],
  },
  {
    id: "mockup-laptop-handy",
    title: "Laptop und Handy",
    category: "mockup",
    tags: ["MacBook", "Laptop", "iPhone", "Website", "Responsive", "Screenshot"],
    width: 1000,
    height: 660,
    anchor: "center",
    nodes: [
      device({
        device: LAPTOP,
        tilt: FRONT,
        x: 0,
        y: 0,
        w: 900,
        shadow: DEVICE_SHADOW,
        name: "MacBook",
        ...motion("zoom", { duration: 700 }),
      }),
      device({
        ...phoneFront,
        x: 1000 - widthAt(phoneFront, 430),
        y: 660 - 430,
        h: 430,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
        ...motion("fly-left", { delay: 350, duration: 600 }),
      }),
    ],
  },
  {
    id: "mockup-browser-adresse",
    title: "Browserfenster mit Adresse",
    category: "mockup",
    tags: ["Website", "Webseite", "Browser", "Launch", "URL", "Relaunch"],
    width: 1000,
    height: 860,
    anchor: "center",
    only: "design",
    nodes: [
      device({
        device: "browser",
        url: "www.deine-website.de",
        x: 0,
        y: 0,
        w: 1000,
        shadow: DEVICE_SHADOW,
        name: "Browser",
      }),
      text({
        text: "Unsere neue Website ist online",
        style: "heading-2",
        size: 56,
        color: "text",
        x: 0,
        y: heightAt({ device: "browser" }, 1000) + 56,
        w: 1000,
        align: "center",
      }),
    ],
  },
  {
    id: "mockup-tablet-quer",
    title: "Tablet quer mit Titel",
    category: "mockup",
    tags: ["iPad", "Tablet", "Querformat", "Screenshot", "App"],
    width: 1000,
    height: 900,
    anchor: "center",
    only: "design",
    nodes: [
      stack({
        x: 0,
        y: 0,
        w: 1000,
        align: "center",
        gap: 14,
        items: [
          line("Jetzt auch auf dem iPad", "heading-1", 62, "text"),
          line("Mehr Platz für deine Ideen", "body", 32, "text-muted"),
        ],
      }),
      device({
        device: "ipad-pro-13",
        landscape: true,
        x: 40,
        y: 900 - heightAt({ device: "ipad-pro-13", landscape: true }, 920),
        w: 920,
        shadow: DEVICE_SHADOW,
        name: "iPad",
      }),
    ],
  },
  {
    id: "mockup-app-feature",
    title: "App-Feature (App-Store-Stil)",
    category: "mockup",
    tags: ["App Store", "Screenshot", "Feature", "iPhone", "Google Play", "Store"],
    width: 900,
    height: 1240,
    anchor: "center",
    only: "design",
    nodes: [
      stack({
        x: 0,
        y: 0,
        w: 900,
        align: "center",
        gap: 16,
        items: [
          line("Alles im Blick", "heading-1", 84, "text"),
          line("Alle Aufgaben deines Teams auf einen Blick", "body", 34, "text-muted"),
        ],
      }),
      device({
        ...phoneFront,
        x: (900 - widthAt(phoneFront, 960)) / 2,
        y: 1240 - 960,
        h: 960,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
      }),
    ],
  },
  {
    id: "mockup-jetzt-verfuegbar",
    title: "Jetzt verfügbar mit Store-Badges",
    category: "mockup",
    tags: ["App Store", "Google Play", "Download", "Launch", "Neue App", "Badge"],
    width: 1000,
    height: 760,
    anchor: "center",
    only: "design",
    nodes: [
      device({
        ...phoneFloat,
        x: 0,
        y: 0,
        h: 760,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
      }),
      stack({
        x: 470,
        y: 300,
        w: 530,
        anchor: "center",
        gap: [18, 24],
        items: [
          line("JETZT VERFÜGBAR", "subline", 26, "primary"),
          line("Die App für dein Team", "heading-1", 70, "text"),
          line("Kostenlos für iPhone und Android", "body", 30, "text-muted"),
        ],
      }),
      ...storeBadge(470, 560, "apple"),
      ...storeBadge(745, 560, "google"),
    ],
  },
  {
    id: "mockup-drei-handys",
    title: "Drei Handys gefächert",
    category: "mockup",
    tags: ["App", "Smartphone", "iPhone", "Screenshots", "Fächer", "Galerie"],
    width: 1000,
    height: 900,
    anchor: "center",
    nodes: [
      ...(
        [
          { x: 71, rotation: -12, finish: "tiefblau", delay: 250 },
          {
            x: 1000 - 71 - widthAt(phoneFront, 700),
            rotation: 12,
            finish: "cosmic-orange",
            delay: 400,
          },
        ] as const
      ).map((side) =>
        device({
          ...phoneFront,
          finish: side.finish,
          x: side.x,
          y: 140,
          h: 700,
          rotation: side.rotation,
          shadow: DEVICE_SHADOW,
          name: "iPhone",
          ...motion("fly-up", { delay: side.delay, duration: 650 }),
        }),
      ),
      device({
        ...phoneFront,
        x: (1000 - widthAt(phoneFront, 840)) / 2,
        y: 30,
        h: 840,
        shadow: DEVICE_SHADOW,
        name: "iPhone Mitte",
        ...motion("fly-up", { duration: 650 }),
      }),
    ],
  },
  {
    id: "mockup-handy-liegend",
    title: "Liegendes Handy",
    category: "mockup",
    tags: ["iPhone", "Smartphone", "Flatlay", "Tisch", "Perspektive", "Schatten"],
    width: 900,
    height: 760,
    anchor: "center",
    nodes: [
      device({
        device: PHONE,
        tilt: LYING,
        x: 0,
        y: 0,
        w: 900,
        h: 760,
        shadow: { color: "#00000059", x: -20, y: 46, blur: 80 },
        name: "iPhone",
        ...motion("drop", { duration: 700 }),
      }),
    ],
  },
  {
    id: "mockup-imac-handy",
    title: "iMac und Handy",
    category: "mockup",
    tags: ["iMac", "Desktop", "Bildschirm", "iPhone", "Website", "Responsive"],
    width: 960,
    height: 780,
    anchor: "center",
    nodes: [
      device({
        device: "imac-24",
        finish: "silber",
        tilt: FRONT,
        x: 0,
        y: 0,
        w: 820,
        shadow: DEVICE_SHADOW,
        name: "iMac",
        ...motion("zoom", { duration: 700 }),
      }),
      device({
        ...phoneFront,
        x: 960 - widthAt(phoneFront, 440),
        y: 780 - 440,
        h: 440,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
        ...motion("fly-left", { delay: 350, duration: 600 }),
      }),
    ],
  },
  {
    id: "mockup-handy-karten",
    title: "Handy mit schwebenden Karten",
    category: "mockup",
    tags: ["App", "Bewertung", "Sterne", "Kennzahl", "iPhone", "Erfolg"],
    width: 1000,
    height: 880,
    anchor: "center",
    duration: 5000,
    nodes: [
      device({
        ...phoneFront,
        x: (1000 - widthAt(phoneFront, 880)) / 2,
        y: 0,
        h: 880,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
        ...motion("fly-up", { duration: 700 }),
      }),
      ...floatingCard(0, 150, 440, {
        big: "4,9 von 5",
        small: "12.000 Bewertungen",
        move: motion("slide-right", { delay: 600 }),
        badge: [
          ellipse({
            x: 28,
            y: 178,
            w: 84,
            h: 84,
            fill: "accent",
            name: "Kreis",
            ...motion("pop", { delay: 750 }),
          }),
          icon({
            icon: "star",
            x: 48,
            y: 198,
            w: 44,
            h: 44,
            color: "on:accent",
            name: "Stern",
            ...motion("pop", { delay: 750 }),
          }),
        ],
      }),
      ...floatingCard(560, 600, 440, {
        big: "+38 %",
        small: "mehr Buchungen",
        move: motion("slide-left", { delay: 1100 }),
        badge: [
          ellipse({
            x: 588,
            y: 628,
            w: 84,
            h: 84,
            fill: "primary",
            name: "Kreis",
            ...motion("pop", { delay: 1250 }),
          }),
          icon({
            icon: "trend-up",
            x: 608,
            y: 648,
            w: 44,
            h: 44,
            color: "on:primary",
            name: "Symbol",
            ...motion("pop", { delay: 1250 }),
          }),
        ],
      }),
    ],
  },
  // --- Video: devices fly in, texts follow ------------------------------------
  {
    id: "mockup-video-handy-einflug",
    title: "Handy fliegt ein mit Titel",
    category: "mockup",
    tags: ["App", "iPhone", "Animation", "Einflug", "Launch", "Reel"],
    width: 900,
    height: 1320,
    anchor: "center",
    duration: 5000,
    only: "video",
    nodes: [
      stack({
        x: 0,
        y: 0,
        w: 900,
        align: "center",
        gap: 16,
        items: [
          line("Neu im App Store", "heading-1", 76, WHITE, {
            ...ON_VIDEO,
            ...motion("slide-up", { delay: 500 }),
          }),
          line("Deine Termine. Endlich im Griff.", "body", 36, "#ffffffe6", {
            ...ON_VIDEO,
            ...motion("fade", { delay: 800 }),
          }),
        ],
      }),
      device({
        ...phoneFront,
        x: (900 - widthAt(phoneFront, 1000)) / 2,
        y: 320,
        h: 1000,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
        ...motion("fly-up", { duration: 800 }),
      }),
    ],
  },
  {
    id: "mockup-video-features",
    title: "Handy mit Feature-Punkten",
    category: "mockup",
    tags: ["App", "Funktionen", "Features", "Vorteile", "iPhone", "Checkliste"],
    width: 1000,
    height: 900,
    anchor: "center",
    duration: 6000,
    only: "video",
    nodes: [
      device({
        ...phoneFront,
        x: (1000 - widthAt(phoneFront, 900)) / 2,
        y: 0,
        h: 900,
        shadow: DEVICE_SHADOW,
        name: "iPhone",
        ...motion("pop", { duration: 600 }),
      }),
      ...[
        { x: 0, y: 150, label: "Termine planen", from: "slide-right" as const },
        { x: 600, y: 400, label: "Team einladen", from: "slide-left" as const },
        { x: 0, y: 650, label: "Alles synchron", from: "slide-right" as const },
      ].flatMap((point, index) =>
        iconPill(point.x, point.y, 400, 100, {
          icon: "check",
          label: point.label,
          fill: "background",
          badge: "primary",
          size: 34,
          move: motion(point.from, { delay: 900 + index * 700 }),
        }),
      ),
    ],
  },
  {
    id: "mockup-video-laptop",
    title: "Laptop mit Titel",
    category: "mockup",
    tags: ["MacBook", "Laptop", "Website", "Software", "Demo", "Animation"],
    width: 1000,
    height: 840,
    anchor: "center",
    duration: 5000,
    only: "video",
    nodes: [
      text({
        text: "So einfach geht’s",
        style: "heading-1",
        size: 72,
        color: WHITE,
        x: 0,
        y: 0,
        w: 1000,
        align: "center",
        ...ON_VIDEO,
        ...motion("slide-up", { delay: 500 }),
      }),
      device({
        device: LAPTOP,
        tilt: FRONT,
        x: 0,
        y: 840 - heightAt({ device: LAPTOP, tilt: FRONT }, 1000),
        w: 1000,
        shadow: DEVICE_SHADOW,
        name: "MacBook",
        ...motion("zoom", { duration: 800 }),
      }),
    ],
  },
  {
    id: "mockup-video-website",
    title: "Website-Launch mit Adresse",
    category: "mockup",
    tags: ["Website", "Browser", "Launch", "URL", "Relaunch", "Online"],
    width: 1000,
    height: 860,
    anchor: "center",
    duration: 5000,
    only: "video",
    nodes: [
      device({
        device: "browser",
        url: "www.deine-website.de",
        x: 0,
        y: 0,
        w: 1000,
        shadow: DEVICE_SHADOW,
        name: "Browser",
        ...motion("pop", { duration: 600 }),
      }),
      ...iconPill(210, 760, 580, 100, {
        icon: "globe",
        label: "Jetzt online",
        fill: "primary",
        badge: "background",
        size: 38,
        move: motion("slide-up", { delay: 700 }),
      }),
    ],
  },
];
